import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { apiGet, apiPost } from "../../api/apiclient";
import { DOWNLOAD_FILE, DOWNLOAD_FILE_RESULT, UPDATE_FILE_RESULT } from "../../api/endpoints";

// Async thunk for logging in
export const pdfDetails = createAsyncThunk(
  "/api/pdfDetails/",
  async (id, { rejectWithValue }) => {
    try {
      const response = await apiGet(`${DOWNLOAD_FILE_RESULT}${id}/`);
      if (response?.data.status === "success") {
        return response?.data;
      } else {
        return rejectWithValue(
          response.data.message || "Unexpected error occurred."
        );
      }
    } catch (error) {
      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        return rejectWithValue(error.response.data.message);
      }
      return rejectWithValue(error.message || "Network error occurred.");
    }
  }
);

// Async thunk for logging in
export const pdfDownload = createAsyncThunk(
  "/api/pdfDownloads/",
  async (id, { rejectWithValue }) => {
    try {
      const response = await apiGet(`${DOWNLOAD_FILE}${id}/`);
      if (response?.data) {
        return response?.data;
      } else {
        return rejectWithValue(
          response.data.message || "Unexpected error occurred."
        );
      }
    } catch (error) {
      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        return rejectWithValue(error.response.data.message);
      }
      return rejectWithValue(error.message || "Network error occurred.");
    }
  }
);

// Async thunk for saving PDF data
export const savePdfData = createAsyncThunk(
  "/api/savePdfData/",
  async ({ id, pdfData }, { rejectWithValue }) => {
    try {
      const response = await apiPost(`${UPDATE_FILE_RESULT}${id}/`, pdfData);
      if (response?.data?.status === "success") {
        return response.data;
      } else {
        return rejectWithValue(response.data.message || "Save failed");
      }
    } catch (error) {
      if (error.response?.data?.message) {
        return rejectWithValue(error.response.data.message);
      }
      return rejectWithValue(error.message || "Network error occurred");
    }
  }
);

const PdfDataSlice = createSlice({
  name: "ai_agents",
  initialState: {
    pdfDataLoading: false,
    pdfDataError: {},
    pdfData: [],
    pdfDataOriginal: [],
    pdfFileLoading: false,
    pdfFileError: {},
    pdfFile: {},
    pdfSaveLoading: false,
    pdfSaveError: null,
    pdfSaveSuccess: false,
  },
  reducers: {
    updatePdfField: (state, action) => {
      const { pageIndex, field, value, lineIndex = null } = action.payload;
      const pages = state.pdfData?.data?.data;
      if (!Array.isArray(pages) || !pages[pageIndex]) return;

      if (lineIndex !== null) {
        const lineItems =
          pages[pageIndex]["Line Items"] || pages[pageIndex]["Items"];
        if (Array.isArray(lineItems) && lineItems[lineIndex]) {
          lineItems[lineIndex][field] = value;
        }
      } else {
        pages[pageIndex][field] = value;
      }
    },
    resetPdfField: (state, action) => {
      const { pageIndex, field, lineIndex = null } = action.payload;

      const pages = state.pdfData?.data?.data;
      const originalPages = state.pdfDataOriginal?.data?.data;

      if (
        !Array.isArray(pages) ||
        !pages[pageIndex] ||
        !Array.isArray(originalPages) ||
        !originalPages[pageIndex]
      ) {
        return;
      }

      const page = pages[pageIndex];
      const originalPage = originalPages[pageIndex];

      if (lineIndex !== null) {
        const lineItems = page["Line Items"] || page["Items"];
        const originalLineItems =
          originalPage["Line Items"] || originalPage["Items"];

        if (
          Array.isArray(lineItems) &&
          lineItems[lineIndex] &&
          Array.isArray(originalLineItems) &&
          originalLineItems[lineIndex]
        ) {
          lineItems[lineIndex][field] =
            originalLineItems[lineIndex][field];
        }
      } else {
        page[field] = originalPage[field];
      }
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(pdfDetails.pending, (state) => {
        state.pdfDataLoading = true;
        state.pdfDataError = null;
      })
      .addCase(pdfDetails.fulfilled, (state, action) => {
        state.pdfDataLoading = false;
        state.pdfData = action.payload;
        state.pdfDataOriginal = JSON.parse(JSON.stringify(action.payload));
      })
      .addCase(pdfDetails.rejected, (state, action) => {
        state.pdfDataLoading = false;
        state.pdfDataError = action.payload
      })
      .addCase(pdfDownload.pending, (state) => {
        state.pdfFileLoading = true;
        state.pdfFileError = null;
      })
      .addCase(pdfDownload.fulfilled, (state, action) => {
        state.pdfFileLoading = false;
        state.pdfFile = action.payload
      })
      .addCase(pdfDownload.rejected, (state, action) => {
        state.pdfFileLoading = false;
        state.pdfFileError = action.payload
      })
      .addCase(savePdfData.pending, (state) => {
        state.pdfSaveLoading = true;
        state.pdfSaveError = null;
      })
      .addCase(savePdfData.fulfilled, (state, action) => {
        state.pdfSaveLoading = false;
        state.pdfSaveSuccess = true;
      })
      .addCase(savePdfData.rejected, (state, action) => {
        state.pdfSaveLoading = false;
        state.pdfSaveError = action.payload;
      });
  },
});
export const { updatePdfField, resetPdfField } = PdfDataSlice.actions;
export default PdfDataSlice.reducer;
